<script setup lang="ts">
import axios from 'axios';
import { computed, nextTick, onUnmounted, ref, watch, watchEffect } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import PdfLivePreview from '@/components/Tools/PdfLivePreview.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolHowToCard from '@/components/Tools/ToolHowToCard.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { usePdfPage } from '@/composables/usePdfPage';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import {
    canvasRectToPdf,
    findRegionAtPoint,
    findRegionsInCanvasRect,
    pdfRectToCanvas,
} from '@/lib/pdfTextLines';
import type { PdfTextRegion } from '@/lib/pdfTextLines';
import { dashboard } from '@/routes';
import { redact as redactPage } from '@/routes/tools/pdf';
import redactTool from '@/routes/tools/pdf/redact';

const { lineWithMax } = usePdfUploadLimit();
const { t, locale } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.redact.seo',
    fallbackTitleKey: 'tools.pdf.redact.title',
    fallbackTitleLabel: 'Redact PDF',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: redactPage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.redact.title',
                title: 'Redact PDF',
                href: redactPage.url(),
            },
        ],
    },
});

type RegionTool = 'lines' | 'words' | 'chars' | 'box';
type Status = 'idle' | 'loading' | 'success' | 'error';

/** Normalized 0–1 coordinates (bottom-left origin) for FPDI page units */
type RedactRegion = {
    page: number;
    x: number;
    y: number;
    w: number;
    h: number;
};

function toNormalizedRegion(
    region: Pick<RedactRegion, 'page' | 'x' | 'y' | 'w' | 'h'>,
    pageW: number,
    pageH: number,
): RedactRegion {
    return {
        page: region.page,
        x: region.x / pageW,
        y: region.y / pageH,
        w: region.w / pageW,
        h: region.h / pageH,
    };
}

function fromNormalizedRegion(
    region: RedactRegion,
    pageW: number,
    pageH: number,
): Pick<RedactRegion, 'x' | 'y' | 'w' | 'h'> {
    return {
        x: region.x * pageW,
        y: region.y * pageH,
        w: region.w * pageW,
        h: region.h * pageH,
    };
}

const file = ref<File | null>(null);
const regionTool = ref<RegionTool>('lines');
const status = ref<Status>('idle');
const statusMessage = ref('');
const previewPage = ref(1);

const pdfCanvas = ref<HTMLCanvasElement | null>(null);
const overlayCanvas = ref<HTMLCanvasElement | null>(null);
const {
    isLoading: previewLoading,
    error: previewError,
    pageCount,
    pageWidthPt,
    pageHeightPt,
    previewZoomPct,
    loadFile,
    renderPage,
    extractTextLines,
    extractTextWords,
    extractTextCharacters,
    unload,
} = usePdfPage();

const totalPreviewPages = computed(() => Math.max(1, pageCount.value));
const pageTextTargets = ref<PdfTextRegion[]>([]);
const selectedRegions = ref<Map<string, RedactRegion>>(new Map());

const dragStart = ref<{ x: number; y: number } | null>(null);
const dragCurrent = ref<{ x: number; y: number } | null>(null);
const textDragPointerDown = ref<{ x: number; y: number } | null>(null);
const textDragPointerCurrent = ref<{ x: number; y: number } | null>(null);
const textDragHitId = ref<string | null>(null);

const isTextTargetTool = computed(
    () =>
        regionTool.value === 'lines' ||
        regionTool.value === 'words' ||
        regionTool.value === 'chars',
);

const pagesDetectedLine = computed(() => {
    const n = pageCount.value;

    if (!n) {
        return '';
    }

    if (n === 1) {
        return t('tools.pdf.ui.pages_detected_one', '1 page detected');
    }

    return t('tools.pdf.ui.pages_detected_multi', '{n} pages detected').replace(
        '{n}',
        String(n),
    );
});

const regionCount = computed(() => selectedRegions.value.size);

const regionPageCount = computed(() => {
    const pages = new Set<number>();

    for (const r of selectedRegions.value.values()) {
        pages.add(r.page);
    }

    return pages.size;
});

const regionCountLine = computed(() => {
    const n = regionCount.value;
    const p = regionPageCount.value;

    if (n === 0) {
        return '';
    }

    return t('tools.pdf.ui.redact_regions_count', '{n} regions on {p} pages')
        .replace('{n}', String(n))
        .replace('{p}', String(p));
});

const overlayInteractive = computed(() => !!file.value);

const canSubmit = computed(
    () => !!file.value && status.value !== 'loading' && regionCount.value > 0,
);

function targetKey(targetId: string): string {
    return `${regionTool.value}:${targetId}`;
}

function isTargetSelected(targetId: string): boolean {
    return selectedRegions.value.has(targetKey(targetId));
}

function addTarget(region: PdfTextRegion): void {
    if (!pageWidthPt.value || !pageHeightPt.value) {
        return;
    }

    const next = new Map(selectedRegions.value);
    next.set(
        targetKey(region.id),
        toNormalizedRegion(region, pageWidthPt.value, pageHeightPt.value),
    );
    selectedRegions.value = next;
}

function removeTarget(targetId: string): void {
    const next = new Map(selectedRegions.value);
    next.delete(targetKey(targetId));
    selectedRegions.value = next;
}

function toggleTarget(region: PdfTextRegion): void {
    if (isTargetSelected(region.id)) {
        removeTarget(region.id);
    } else {
        addTarget(region);
    }
}

function canvasMarqueeRect(
    a: { x: number; y: number },
    b: { x: number; y: number },
): { x: number; y: number; w: number; h: number } {
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);

    return {
        x,
        y,
        w: Math.abs(b.x - a.x),
        h: Math.abs(b.y - a.y),
    };
}

function selectTargetsInCanvasRect(rect: {
    x: number;
    y: number;
    w: number;
    h: number;
}): void {
    if (
        rect.w < 2 ||
        rect.h < 2 ||
        !pdfCanvas.value ||
        !pageWidthPt.value ||
        !pageHeightPt.value
    ) {
        return;
    }

    const hits = findRegionsInCanvasRect(
        pageTextTargets.value,
        rect,
        pageWidthPt.value,
        pageHeightPt.value,
        pdfCanvas.value.width,
        pdfCanvas.value.height,
    );

    if (hits.length === 0) {
        return;
    }

    const next = new Map(selectedRegions.value);

    for (const t of hits) {
        next.set(
            targetKey(t.id),
            toNormalizedRegion(t, pageWidthPt.value, pageHeightPt.value),
        );
    }

    selectedRegions.value = next;
}

function textDragMarqueePreviewIds(): Set<string> {
    const down = textDragPointerDown.value;
    const current = textDragPointerCurrent.value;

    if (
        !down ||
        !current ||
        !pdfCanvas.value ||
        !pageWidthPt.value ||
        !pageHeightPt.value
    ) {
        return new Set();
    }

    const rect = canvasMarqueeRect(down, current);

    return new Set(
        findRegionsInCanvasRect(
            pageTextTargets.value,
            rect,
            pageWidthPt.value,
            pageHeightPt.value,
            pdfCanvas.value.width,
            pdfCanvas.value.height,
        ).map((t) => t.id),
    );
}

async function loadTextTargets(page: number): Promise<void> {
    if (regionTool.value === 'lines') {
        pageTextTargets.value = await extractTextLines(page);
    } else if (regionTool.value === 'words') {
        pageTextTargets.value = await extractTextWords(page);
    } else if (regionTool.value === 'chars') {
        pageTextTargets.value = await extractTextCharacters(page);
    } else {
        pageTextTargets.value = [];
    }
}

function findTargetAtPointer(pt: {
    x: number;
    y: number;
}): PdfTextRegion | null {
    if (!pdfCanvas.value || !pageWidthPt.value || !pageHeightPt.value) {
        return null;
    }

    return findRegionAtPoint(
        pageTextTargets.value,
        pt,
        pageWidthPt.value,
        pageHeightPt.value,
        pdfCanvas.value.width,
        pdfCanvas.value.height,
    );
}

function clearPageRegions(): void {
    const page = previewPage.value;
    const next = new Map(selectedRegions.value);

    for (const [key, region] of next) {
        if (region.page === page) {
            next.delete(key);
        }
    }

    selectedRegions.value = next;
}

function clearAllRegions(): void {
    selectedRegions.value = new Map();
}

function getOverlayPoint(event: MouseEvent | PointerEvent): {
    x: number;
    y: number;
} {
    const canvas = overlayCanvas.value!;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY,
    };
}

function onOverlayPointerDown(event: PointerEvent): void {
    if (!file.value) {
        return;
    }

    const pt = getOverlayPoint(event);

    if (regionTool.value === 'box') {
        dragStart.value = pt;
        dragCurrent.value = pt;
        drawRedactOverlay();

        return;
    }

    if (!isTextTargetTool.value) {
        return;
    }

    textDragPointerDown.value = pt;
    textDragPointerCurrent.value = pt;
    textDragHitId.value = findTargetAtPointer(pt)?.id ?? null;
    overlayCanvas.value?.setPointerCapture(event.pointerId);
    event.preventDefault();
    drawRedactOverlay();
}

function onOverlayPointerMove(event: PointerEvent): void {
    if (!file.value) {
        return;
    }

    if (regionTool.value === 'box' && dragStart.value) {
        dragCurrent.value = getOverlayPoint(event);
        drawRedactOverlay();

        return;
    }

    if (!textDragPointerDown.value || !isTextTargetTool.value) {
        return;
    }

    textDragPointerCurrent.value = getOverlayPoint(event);
    drawRedactOverlay();
}

function onOverlayPointerUp(event: PointerEvent): void {
    if (overlayCanvas.value?.hasPointerCapture(event.pointerId)) {
        overlayCanvas.value.releasePointerCapture(event.pointerId);
    }

    if (!file.value) {
        return;
    }

    if (regionTool.value === 'box' && dragStart.value) {
        const end = getOverlayPoint(event);
        const x = Math.min(dragStart.value.x, end.x);
        const y = Math.min(dragStart.value.y, end.y);
        const w = Math.abs(end.x - dragStart.value.x);
        const h = Math.abs(end.y - dragStart.value.y);

        dragStart.value = null;
        dragCurrent.value = null;

        if (
            w >= 4 &&
            h >= 4 &&
            pageWidthPt.value > 0 &&
            pageHeightPt.value > 0 &&
            pdfCanvas.value
        ) {
            const pdfRect = canvasRectToPdf(
                { x, y, w, h },
                pageWidthPt.value,
                pageHeightPt.value,
                pdfCanvas.value.width,
                pdfCanvas.value.height,
            );
            const key = `box:${crypto.randomUUID()}`;
            const next = new Map(selectedRegions.value);
            next.set(
                key,
                toNormalizedRegion(
                    { page: previewPage.value, ...pdfRect },
                    pageWidthPt.value,
                    pageHeightPt.value,
                ),
            );
            selectedRegions.value = next;
        }

        drawRedactOverlay();

        return;
    }

    if (textDragPointerDown.value && isTextTargetTool.value) {
        const down = textDragPointerDown.value;
        const up = getOverlayPoint(event);
        const moved =
            Math.abs(up.x - down.x) > 4 || Math.abs(up.y - down.y) > 4;

        if (moved) {
            selectTargetsInCanvasRect(canvasMarqueeRect(down, up));
        } else if (textDragHitId.value) {
            const t = pageTextTargets.value.find(
                (r) => r.id === textDragHitId.value,
            );

            if (t) {
                toggleTarget(t);
            }
        }
    }

    textDragPointerDown.value = null;
    textDragPointerCurrent.value = null;
    textDragHitId.value = null;
    drawRedactOverlay();
}

function drawRedactOverlay(): void {
    if (!overlayCanvas.value || !pdfCanvas.value || !pageCount.value) {
        return;
    }

    const pw = pdfCanvas.value.width;
    const ph = pdfCanvas.value.height;

    if (pw === 0 || ph === 0) {
        return;
    }

    overlayCanvas.value.width = pw;
    overlayCanvas.value.height = ph;
    const ctx = overlayCanvas.value.getContext('2d');

    if (!ctx) {
        return;
    }

    ctx.clearRect(0, 0, pw, ph);

    if (!pageWidthPt.value || !pageHeightPt.value) {
        return;
    }

    const drawRegion = (region: RedactRegion, selected: boolean) => {
        const pt = fromNormalizedRegion(
            region,
            pageWidthPt.value,
            pageHeightPt.value,
        );
        const c = pdfRectToCanvas(
            { ...pt, page: region.page },
            pageWidthPt.value,
            pageHeightPt.value,
            pw,
            ph,
        );

        if (selected) {
            ctx.fillStyle = 'rgba(15, 23, 42, 0.82)';
            ctx.fillRect(c.x, c.y, c.w, c.h);
            ctx.strokeStyle = '#6366f1';
            ctx.lineWidth = 2;
            ctx.strokeRect(c.x, c.y, c.w, c.h);
        }
    };

    const drawPreviewPtRegion = (region: PdfTextRegion) => {
        if (!pageWidthPt.value || !pageHeightPt.value) {
            return;
        }

        const c = pdfRectToCanvas(
            region,
            pageWidthPt.value,
            pageHeightPt.value,
            pw,
            ph,
        );
        ctx.fillStyle = 'rgba(99, 102, 241, 0.28)';
        ctx.fillRect(c.x, c.y, c.w, c.h);
        ctx.strokeStyle = '#6366f1';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(c.x, c.y, c.w, c.h);
    };

    for (const [key, region] of selectedRegions.value) {
        if (region.page !== previewPage.value) {
            continue;
        }

        drawRegion(region, true);
        void key;
    }

    if (isTextTargetTool.value) {
        for (const target of pageTextTargets.value) {
            if (!textDragMarqueePreviewIds().has(target.id)) {
                continue;
            }

            drawPreviewPtRegion(target);
        }

        const down = textDragPointerDown.value;
        const current = textDragPointerCurrent.value;

        if (down && current) {
            const x = Math.min(down.x, current.x);
            const y = Math.min(down.y, current.y);
            const w = Math.abs(current.x - down.x);
            const h = Math.abs(current.y - down.y);
            ctx.strokeStyle = '#6366f1';
            ctx.lineWidth = 2;
            ctx.setLineDash([6, 4]);
            ctx.strokeRect(x, y, w, h);
            ctx.setLineDash([]);
            ctx.fillStyle = 'rgba(99, 102, 241, 0.12)';
            ctx.fillRect(x, y, w, h);
        }
    }

    if (dragStart.value && dragCurrent.value) {
        const x = Math.min(dragStart.value.x, dragCurrent.value.x);
        const y = Math.min(dragStart.value.y, dragCurrent.value.y);
        const w = Math.abs(dragCurrent.value.x - dragStart.value.x);
        const h = Math.abs(dragCurrent.value.y - dragStart.value.y);
        ctx.strokeStyle = '#6366f1';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(x, y, w, h);
        ctx.setLineDash([]);
        ctx.fillStyle = 'rgba(99, 102, 241, 0.15)';
        ctx.fillRect(x, y, w, h);
    }
}

watchEffect(() => {
    void regionTool.value;
    void previewPage.value;
    void pageCount.value;
    void pdfCanvas.value;
    void overlayCanvas.value;
    void selectedRegions.value;
    void pageTextTargets.value;
    void textDragPointerDown.value;
    void textDragPointerCurrent.value;
    void pageWidthPt.value;
    void pageHeightPt.value;
    void locale.value;
    drawRedactOverlay();
});

async function onFileSelected(files: File[]): Promise<void> {
    file.value = files[0] ?? null;
    status.value = 'idle';
    selectedRegions.value = new Map();

    if (!file.value) {
        unload();
        pageTextTargets.value = [];

        return;
    }

    await loadFile(file.value);
    await nextTick();

    if (!pdfCanvas.value) {
        return;
    }

    await renderPreviewPage(1);
}

function removeFile(): void {
    file.value = null;
    status.value = 'idle';
    previewPage.value = 1;
    pageTextTargets.value = [];
    selectedRegions.value = new Map();
    unload();
}

async function renderPreviewPage(page: number): Promise<void> {
    if (!pdfCanvas.value) {
        return;
    }

    const clamped = Math.min(totalPreviewPages.value, Math.max(1, page));
    previewPage.value = clamped;
    await renderPage(pdfCanvas.value, clamped, 560);
    await loadTextTargets(clamped);
    await nextTick();
    drawRedactOverlay();
}

watch(regionTool, async () => {
    if (!file.value) {
        return;
    }

    await loadTextTargets(previewPage.value);
    drawRedactOverlay();
});

watch(previewZoomPct, () => {
    if (!file.value || !pdfCanvas.value) {
        return;
    }

    void renderPreviewPage(previewPage.value);
});

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        try {
            const parsed = JSON.parse(await (e.response.data as Blob).text());
            const msgs = Object.values(parsed.errors ?? {}).flat();

            return (
                (msgs[0] as string) ||
                t('tools.pdf.ui.redact_failed', 'Redaction failed.')
            );
        } catch {
            return t('tools.pdf.ui.redact_failed', 'Redaction failed.');
        }
    }

    return t('tools.pdf.ui.redact_failed', 'Redaction failed.');
}

async function redact(): Promise<void> {
    if (!file.value || !canSubmit.value) {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t(
        'tools.pdf.ui.redacting_regions',
        'Redacting regions...',
    );

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append(
        'regions',
        JSON.stringify([...selectedRegions.value.values()]),
    );

    try {
        const response = await axios.post(redactTool.submit.url(), formData, {
            responseType: 'blob',
        });

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'redacted.pdf';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.redacted_success',
            'PDF redacted successfully!',
        );
        file.value = null;
        previewPage.value = 1;
        pageTextTargets.value = [];
        selectedRegions.value = new Map();
        unload();
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}

onUnmounted(unload);
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-redact" />

    <ToolLayout
        :title="t('tools.pdf.redact.title', 'Redact PDF')"
        :subtitle="t('tools.pdf.redact.description', '')"
        :show-how-to="false"
    >
        <div class="grid gap-4 lg:grid-cols-[3fr_2fr]">
            <div class="lg:sticky lg:top-4 lg:self-start">
                <PdfLivePreview
                    v-model:zoom="previewZoomPct"
                    :has-file="!!file"
                    :loading="previewLoading"
                    :error="previewError"
                    :current-page="previewPage"
                    :total-pages="pageCount"
                    :rendering-message="
                        t('tools.pdf.ui.rendering', 'Rendering…')
                    "
                    @change-page="renderPreviewPage"
                >
                    <div
                        class="flex min-h-48 items-center justify-center px-2 py-4"
                    >
                        <div class="relative inline-block max-w-full">
                            <canvas
                                ref="pdfCanvas"
                                class="relative z-0 block h-auto max-h-none rounded-md bg-white shadow-md dark:bg-gray-950"
                            />
                            <canvas
                                ref="overlayCanvas"
                                class="absolute top-0 left-0 z-10 max-w-full"
                                :class="
                                    overlayInteractive
                                        ? regionTool === 'box'
                                            ? 'cursor-crosshair'
                                            : 'cursor-text'
                                        : 'pointer-events-none'
                                "
                                style="touch-action: none"
                                @pointerdown="onOverlayPointerDown"
                                @pointermove="onOverlayPointerMove"
                                @pointerup="onOverlayPointerUp"
                                @pointercancel="onOverlayPointerUp"
                            />
                        </div>
                    </div>
                    <template #footer>
                        <p
                            v-if="file && !previewLoading"
                            class="mt-3 text-center text-xs text-gray-400 dark:text-gray-500"
                        >
                            <span v-if="regionCountLine">{{
                                regionCountLine
                            }}</span>
                            <span v-else-if="regionTool === 'lines'">{{
                                t(
                                    'tools.pdf.ui.redact_footer_lines_hint',
                                    'Click or drag across lines. Use Words or Letters for finer selection.',
                                )
                            }}</span>
                            <span v-else-if="regionTool === 'words'">{{
                                t(
                                    'tools.pdf.ui.redact_footer_words_hint',
                                    'Click a word or drag a box over only the words you want to redact.',
                                )
                            }}</span>
                            <span v-else-if="regionTool === 'chars'">{{
                                t(
                                    'tools.pdf.ui.redact_footer_chars_hint',
                                    'Click a letter or drag a tight box to redact only those characters.',
                                )
                            }}</span>
                            <span v-else>{{
                                t(
                                    'tools.pdf.ui.redact_footer_box_hint',
                                    'Drag on the preview to draw redaction boxes.',
                                )
                            }}</span>
                        </p>
                        <p
                            v-if="
                                file &&
                                !previewLoading &&
                                isTextTargetTool &&
                                pageTextTargets.length === 0
                            "
                            class="mt-2 text-center text-xs text-amber-600 dark:text-amber-400"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.redact_no_text_lines',
                                    'No selectable text on this page — use Draw box.',
                                )
                            }}
                        </p>
                    </template>
                </PdfLivePreview>
                <div class="mt-4">
                    <ToolHowToCard />
                </div>
            </div>

            <div class="space-y-4">
                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <DropZone
                        accept="application/pdf"
                        :multiple="false"
                        :label="
                            t(
                                'tools.pdf.ui.drop_pdf',
                                'Click or drag a PDF here',
                            )
                        "
                        :sublabel="
                            lineWithMax(t('tools.pdf.ui.pdf_file', 'PDF file'))
                        "
                        @update:model-value="onFileSelected"
                    />
                    <div v-if="file" class="mt-3">
                        <FileCard
                            :file="file"
                            :show-drag-handle="false"
                            @remove="removeFile"
                        />
                    </div>
                    <p
                        v-if="file && pageCount > 0 && !previewLoading"
                        class="mt-2 text-xs text-gray-500 dark:text-gray-400"
                    >
                        {{ pagesDetectedLine }}
                    </p>
                </div>

                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <p
                        class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                        {{
                            t(
                                'tools.pdf.ui.redact_region_tool_heading',
                                'Selection tool',
                            )
                        }}
                    </p>
                    <div class="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            class="rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors"
                            :class="
                                regionTool === 'lines'
                                    ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                    : 'border-border bg-card text-muted-foreground hover:border-brand-300 dark:text-foreground/85'
                            "
                            @click="regionTool = 'lines'"
                        >
                            <span class="block">{{
                                t('tools.pdf.ui.redact_tool_lines', 'Lines')
                            }}</span>
                            <span class="text-xs text-gray-400">{{
                                t(
                                    'tools.pdf.ui.redact_tool_lines_desc',
                                    'Whole line',
                                )
                            }}</span>
                        </button>
                        <button
                            type="button"
                            class="rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors"
                            :class="
                                regionTool === 'words'
                                    ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                    : 'border-border bg-card text-muted-foreground hover:border-brand-300 dark:text-foreground/85'
                            "
                            @click="regionTool = 'words'"
                        >
                            <span class="block">{{
                                t('tools.pdf.ui.redact_tool_words', 'Words')
                            }}</span>
                            <span class="text-xs text-gray-400">{{
                                t(
                                    'tools.pdf.ui.redact_tool_words_desc',
                                    'Per word',
                                )
                            }}</span>
                        </button>
                        <button
                            type="button"
                            class="rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors"
                            :class="
                                regionTool === 'chars'
                                    ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                    : 'border-border bg-card text-muted-foreground hover:border-brand-300 dark:text-foreground/85'
                            "
                            @click="regionTool = 'chars'"
                        >
                            <span class="block">{{
                                t('tools.pdf.ui.redact_tool_chars', 'Letters')
                            }}</span>
                            <span class="text-xs text-gray-400">{{
                                t(
                                    'tools.pdf.ui.redact_tool_chars_desc',
                                    'Per character',
                                )
                            }}</span>
                        </button>
                        <button
                            type="button"
                            class="rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors"
                            :class="
                                regionTool === 'box'
                                    ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                    : 'border-border bg-card text-muted-foreground hover:border-brand-300 dark:text-foreground/85'
                            "
                            @click="regionTool = 'box'"
                        >
                            <span class="block">{{
                                t('tools.pdf.ui.redact_tool_box', 'Draw box')
                            }}</span>
                            <span class="text-xs text-gray-400">{{
                                t(
                                    'tools.pdf.ui.redact_tool_box_desc',
                                    'Freehand area',
                                )
                            }}</span>
                        </button>
                    </div>

                    <p
                        v-if="regionCountLine"
                        class="mt-3 text-sm text-gray-600 dark:text-gray-400"
                    >
                        {{ regionCountLine }}
                    </p>

                    <div
                        v-if="regionCount > 0"
                        class="mt-3 flex flex-wrap gap-2"
                    >
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            @click="clearPageRegions"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.redact_clear_page',
                                    'Clear this page',
                                )
                            }}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            @click="clearAllRegions"
                        >
                            {{
                                t('tools.pdf.ui.redact_clear_all', 'Clear all')
                            }}
                        </Button>
                    </div>
                </div>

                <div
                    class="rounded-xl border border-slate-200/80 bg-slate-50 p-4 text-sm leading-relaxed text-slate-600 dark:border-border dark:bg-muted/50 dark:text-gray-300"
                >
                    <strong
                        class="font-medium text-slate-800 dark:text-gray-100"
                        >{{
                            t('tools.pdf.ui.redact_note_title', 'Note:')
                        }}</strong
                    >
                    {{ t('tools.pdf.ui.redact_note', '') }}
                </div>

                <Button
                    class="w-full bg-brand-600 text-white hover:bg-brand-700"
                    :disabled="!canSubmit"
                    @click="redact"
                >
                    {{ t('tools.pdf.ui.redact_pdf_button', 'Redact PDF') }}
                </Button>

                <StatusMessage :status="status" :message="statusMessage" />
            </div>
        </div>
    </ToolLayout>
</template>
